<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="open"
        class="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-[60] flex items-center justify-center px-4"
        @click="emit('close')"
      >
        <div class="w-full max-w-md bg-white dark:bg-[#1C2128] rounded-xl shadow-2xl p-5" @click.stop>
          <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Share this task</h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Anyone with the link can view a read only copy. They cannot edit it or see anything else in your account.
          </p>

          <div class="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
            <span class="text-[13px] font-medium text-gray-700 dark:text-gray-200">
              {{ isShared ? 'Link is on' : 'Create a public link' }}
            </span>
            <Toggle :model-value="isShared" @update:model-value="onToggle" />
          </div>

          <template v-if="isShared">
            <div class="flex items-baseline gap-2 mb-3 px-1">
              <span class="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{{ views }}</span>
              <span class="text-[13px] text-gray-500 dark:text-gray-400">
                {{ views === 1 ? 'person has viewed this' : 'people have viewed this' }}
              </span>
            </div>

            <div class="flex items-stretch gap-2 mb-3">
              <input
                ref="urlInput"
                :value="publicUrl"
                readonly
                @focus="$event.target.select()"
                class="flex-1 min-w-0 px-3 py-2 text-[13px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button
                type="button"
                @click="copy"
                class="px-3.5 py-2 text-sm font-semibold rounded-lg transition-all duration-150 active:scale-[0.97] flex-shrink-0"
                :class="copied
                  ? 'bg-green-600 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md text-white'"
              >{{ copied ? 'Copied' : 'Copy' }}</button>
            </div>

            <label class="flex items-start gap-2.5 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-white/[0.03] cursor-pointer transition-colors duration-150 mb-4">
              <input
                type="checkbox"
                :checked="indexable"
                @change="onIndexableChange($event.target.checked)"
                class="mt-0.5 rounded text-blue-600 focus:ring-blue-500"
              />
              <span class="min-w-0">
                <span class="block text-[13px] font-medium text-gray-700 dark:text-gray-200">Let search engines find this</span>
                <span class="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
                  On by default. Turn it off to keep the link working for people you send it to while staying out of search results.
                </span>
              </span>
            </label>
          </template>

          <div class="flex justify-end gap-2">
            <button
              type="button"
              @click="emit('close')"
              class="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >Done</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useTasksStore } from 'stores/tasks.js'
import Toggle from './Toggle.vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  task: { type: Object, default: null },
})
const emit = defineEmits(['close'])

const store = useTasksStore()
const urlInput = ref(null)
const copied = ref(false)
let copiedTimer = null

const isShared = computed(() => !!props.task?.shared_publicly)
const publicUrl = computed(() => props.task?.public_url || '')
const indexable = computed(() => props.task?.public_indexable !== false)
// Unique visitors, counted server side. Only the single-task endpoints return
// it (TaskBlueprint's :detailed view), so it is absent on a task that came
// from the checklist or board cache.
const views = computed(() => props.task?.unique_public_views ?? 0)

async function onToggle(next) {
  if (!props.task) return
  if (next) {
    const updated = await store.shareTask(props.task)
    // Select the link straight away: the reason anyone opens this dialog is
    // to copy the URL.
    if (updated) {
      await nextTick()
      urlInput.value?.select()
    }
  } else {
    await store.unshareTask(props.task)
  }
}

function onIndexableChange(checked) {
  if (props.task) store.shareTask(props.task, checked)
}

async function copy() {
  if (!publicUrl.value) return
  try {
    await navigator.clipboard.writeText(publicUrl.value)
  } catch {
    // Clipboard API needs a secure context and can be blocked outright.
    // Selecting the text leaves the user one keystroke from copying it,
    // which beats a dialog that silently did nothing.
    urlInput.value?.select()
    return
  }
  copied.value = true
  clearTimeout(copiedTimer)
  copiedTimer = setTimeout(() => { copied.value = false }, 2000)
}

watch(() => props.open, (open) => { if (!open) copied.value = false })
</script>

<style scoped>
.fade-enter-active { transition: opacity 0.15s ease; }
.fade-leave-active { transition: opacity 0.12s ease; }
.fade-enter-from,
.fade-leave-to     { opacity: 0; }
</style>
